import { useRef, useState, useEffect } from 'react';

const OBSERVER_CONFIG = { attributes: false, childList: true, subtree: false };
const WINDOW_RESIZE_DELAY = 100;

const resetListeners = (listenedFunction: () => void, timer: NodeJS.Timeout, callback: () => void) => {
  window.removeEventListener('resize', listenedFunction);
  clearTimeout(timer);
  if (callback) {
    callback();
  }
};

/**
 * Hook to find the tallest element in a collection of Refs
 *
 * Once measurement is complete, add:
 *  - a window resize listener so we can re-measure if view breakpoint changes
 *  - mutation observers to all refs so we can re-measure if the element contents change
 *
 * Set removeHeight to TRUE in order to remove the height from the element so we can recalculate the height
 */
export const useTallestElementHeight = () => {
  const [tallestElementHeight, setTallestElementHeight] = useState('auto');
  const [removeHeight, setRemoveHeight] = useState(true);
  const elementRefs = useRef<Array<any | null>>([]);
  const observers: MutationObserver[] = [];
  let resizeTimer: NodeJS.Timeout;

  const clearObservers = () => {
    observers.forEach(observer => observer.disconnect());
    observers.length = 0;
  };

  // Recalculate max height of all referenced elements
  // Triggers on:
  //  - new refs
  //  - window resize
  //  - ref element content changes
  const calculateMaxHeight = () => {
    let maxHeight = 0;

    elementRefs.current.forEach(elementRef => {
      if (elementRef) {
        const { height } = elementRef.getBoundingClientRect();
        maxHeight = Math.max(maxHeight, height);

        // Watch element for content changes and re-trigger height calculation
        // This is for alerts that have a delayed load/render and so may change height
        const observer = new MutationObserver(reCalculateSize);
        observers.push(observer);
        observer.observe(elementRef, OBSERVER_CONFIG);
      }
    });

    setRemoveHeight(false);
    setTallestElementHeight(() => `${maxHeight}px`);
    window.addEventListener('resize', reCalculateSize);
  };

  // Triggered on window resize or ref content changes such as loaded content. Clear everything and re-measure
  const reCalculateSize = () => {
    // Clear the existing timer to prevent rapid calls during resizing
    resetListeners(reCalculateSize, resizeTimer, clearObservers);
    // Set a new timeout to call calculateMaxHeight after a delay
    resizeTimer = setTimeout(() => {
      setRemoveHeight(true);
      setTimeout(calculateMaxHeight);
    }, WINDOW_RESIZE_DELAY);
  };

  useEffect(() => {
    // If refs have changed, reset everything
    resetListeners(reCalculateSize, resizeTimer, clearObservers);

    // this replaces the initialisation of the tallestElementHeight
    // so that it is not set to 0px before the DOM renders
    requestAnimationFrame(() => {
      setTimeout(calculateMaxHeight);
    });

    return resetListeners(reCalculateSize, resizeTimer, clearObservers);
  }, [calculateMaxHeight, elementRefs, reCalculateSize]);

  return { elementRefs, removeHeight, tallestElementHeight };
};
